import React, { useMemo, useState, Component } from 'react';
import { UserIcon, UsersIcon, BookIcon, BuildingIcon, HomeIcon, DollarSignIcon, CalendarIcon, AlertTriangleIcon, PlusIcon, EditIcon, TrashIcon, CopyIcon, CheckIcon, XIcon, ChevronRightIcon, ArrowUpRightIcon, LayersIcon, UserCheckIcon, ClockIcon } from 'lucide-react';
import { StatusBadge } from '../StatusBadge';
import { formatCurrency } from '../utils/currencyFormatter';
import { getDefaultCurrency } from '../../utils/appSettings';
import { GLCodeSelector } from './GLCodeSelector';
import { ChartOfAccounts } from './ChartOfAccounts';
export function DelegationsManager({
  locale
}) {
  const [activeTab, setActiveTab] = useState('delegations');
  const [showForm, setShowForm] = useState(false);
  const [editingDelegation, setEditingDelegation] = useState(null);
  const [selectedEntity, setSelectedEntity] = useState('');
  const [selectedSite, setSelectedSite] = useState('');
  // Sample data for delegations
  const [delegations, setDelegations] = useState([{
    id: 'del-001',
    type: 'user',
    assignee: 'John Smith',
    assigneeId: 'user-001',
    glCode: '1000.1100.1110',
    glName: 'Cash and Cash Equivalents',
    entityId: 'ent-001',
    entityName: 'Global Technologies Inc.',
    siteId: 'site-001',
    siteName: 'New York HQ',
    lowerLimit: 0,
    upperLimit: 5000,
    canApproveAboveLimit: false,
    requiresFourEyes: false,
    startDate: '2023-01-01',
    endDate: '2023-12-31',
    isActive: true,
    currency: 'AUD'
  }, {
    id: 'del-002',
    type: 'role',
    assignee: 'Finance Manager',
    assigneeId: 'role-002',
    glCode: '5000',
    glName: 'Expenses',
    entityId: 'ent-001',
    entityName: 'Global Technologies Inc.',
    siteId: 'site-002',
    siteName: 'San Francisco Office',
    lowerLimit: 5000,
    upperLimit: 25000,
    canApproveAboveLimit: false,
    requiresFourEyes: true,
    secondApproverId: 'user-002',
    secondApprover: 'Jane Doe',
    secondApproverType: 'user',
    startDate: '2023-01-01',
    endDate: null,
    isActive: true,
    currency: 'AUD'
  }, {
    id: 'del-003',
    type: 'user',
    assignee: 'Jane Doe',
    assigneeId: 'user-002',
    glCode: '4000',
    glName: 'Revenue',
    entityId: 'ent-002',
    entityName: 'European Operations Ltd',
    siteId: null,
    siteName: null,
    lowerLimit: 0,
    upperLimit: 10000,
    canApproveAboveLimit: true,
    requiresFourEyes: false,
    startDate: '2023-01-01',
    endDate: '2023-12-31',
    isActive: true,
    currency: 'EUR'
  }, {
    id: 'del-004',
    type: 'group',
    assignee: 'Finance Approvers',
    assigneeId: 'group-001',
    glCode: '5000.5100',
    glName: 'Operating Expenses',
    entityId: 'ent-001',
    entityName: 'Global Technologies Inc.',
    siteId: null,
    siteName: null,
    lowerLimit: 10000,
    upperLimit: 50000,
    canApproveAboveLimit: false,
    requiresFourEyes: true,
    secondApproverId: 'group-002',
    secondApprover: 'Executive Approvers',
    secondApproverType: 'group',
    startDate: '2023-01-01',
    endDate: null,
    isActive: true,
    currency: 'AUD'
  }]);
  // Sample data for GL restrictions
  const [glRestrictions, setGlRestrictions] = useState([{
    id: 'res-001',
    type: 'user',
    assignee: 'John Smith',
    assigneeId: 'user-001',
    glCode: '5000.5200',
    glName: 'Travel Expenses',
    canApprove: false
  }, {
    id: 'res-002',
    type: 'role',
    assignee: 'Junior Accountant',
    assigneeId: 'role-003',
    glCode: '1000.1200',
    glName: 'Non-Current Assets',
    canApprove: false
  }]);
  // Sample data for user leave status
  const [userLeaveStatus, setUserLeaveStatus] = useState([{
    id: 'leave-001',
    userId: 'user-001',
    userName: 'John Smith',
    isOnLeave: true,
    leaveStartDate: '2023-10-15',
    leaveEndDate: '2023-10-30',
    shouldSkip: true
  }, {
    id: 'leave-002',
    userId: 'user-002',
    userName: 'Jane Doe',
    isOnLeave: false,
    leaveStartDate: null,
    leaveEndDate: null,
    shouldSkip: false
  }]);
  // Sample data for approval rules
  const [approvalRules, setApprovalRules] = useState([{
    id: 'rule-001',
    delegationId: 'del-001',
    delegationName: 'John Smith - Cash and Cash Equivalents',
    approvalLevel: 1,
    requiresMultipleApprovers: false,
    escalationUserId: 'user-002',
    escalationUserName: 'Jane Doe',
    autoApproveAfter: 3
  }, {
    id: 'rule-002',
    delegationId: 'del-002',
    delegationName: 'Finance Manager - Expenses',
    approvalLevel: 2,
    requiresMultipleApprovers: true,
    escalationUserId: null,
    escalationUserName: null,
    autoApproveAfter: 5
  }]);
  // Sample data for approval groups
  const [approvalGroups, setApprovalGroups] = useState([{
    id: 'group-001',
    name: 'Finance Approvers',
    members: [{
      id: 'user-001',
      name: 'John Smith'
    }, {
      id: 'user-002',
      name: 'Jane Doe'
    }]
  }, {
    id: 'group-002',
    name: 'Executive Approvers',
    members: [{
      id: 'user-003',
      name: 'Michael Johnson'
    }, {
      id: 'user-004',
      name: 'Sarah Williams'
    }]
  }, {
    id: 'group-003',
    name: 'Procurement Team',
    members: [{
      id: 'user-002',
      name: 'Jane Doe'
    }, {
      id: 'user-003',
      name: 'Michael Johnson'
    }]
  }]);
  // Sample data for entities and sites
  const entities = [{
    id: 'ent-001',
    name: 'Global Technologies Inc.',
    sites: [{
      id: 'site-001',
      name: 'New York HQ'
    }, {
      id: 'site-002',
      name: 'San Francisco Office'
    }]
  }, {
    id: 'ent-002',
    name: 'European Operations Ltd',
    sites: [{
      id: 'site-003',
      name: 'London Office'
    }, {
      id: 'site-004',
      name: 'Paris Branch'
    }]
  }];
  // Sample data for users and roles
  const users = [{
    id: 'user-001',
    name: 'John Smith'
  }, {
    id: 'user-002',
    name: 'Jane Doe'
  }, {
    id: 'user-003',
    name: 'Michael Johnson'
  }, {
    id: 'user-004',
    name: 'Sarah Williams'
  }];
  const roles = [{
    id: 'role-001',
    name: 'Administrator'
  }, {
    id: 'role-002',
    name: 'Finance Manager'
  }, {
    id: 'role-003',
    name: 'Junior Accountant'
  }, {
    id: 'role-004',
    name: 'Department Head'
  }];
  // Sample data for GL codes
  const glCodes = [{
    id: 'gl-001',
    code: '1000',
    name: 'Assets',
    children: [{
      id: 'gl-002',
      code: '1000.1100',
      name: 'Current Assets',
      children: [{
        id: 'gl-003',
        code: '1000.1100.1110',
        name: 'Cash and Cash Equivalents'
      }, {
        id: 'gl-004',
        code: '1000.1100.1120',
        name: 'Accounts Receivable'
      }]
    }, {
      id: 'gl-005',
      code: '1000.1200',
      name: 'Non-Current Assets'
    }]
  }, {
    id: 'gl-006',
    code: '2000',
    name: 'Liabilities',
    children: [{
      id: 'gl-007',
      code: '2000.2100',
      name: 'Current Liabilities'
    }]
  }, {
    id: 'gl-008',
    code: '3000',
    name: 'Equity'
  }, {
    id: 'gl-009',
    code: '4000',
    name: 'Revenue'
  }, {
    id: 'gl-010',
    code: '5000',
    name: 'Expenses',
    children: [{
      id: 'gl-011',
      code: '5000.5100',
      name: 'Operating Expenses'
    }, {
      id: 'gl-012',
      code: '5000.5200',
      name: 'Travel Expenses'
    }]
  }];
  // Available currencies
  const currencies = [{
    code: 'AUD',
    name: 'Australian Dollar'
  }, {
    code: 'USD',
    name: 'US Dollar'
  }, {
    code: 'EUR',
    name: 'Euro'
  }, {
    code: 'GBP',
    name: 'British Pound'
  }, {
    code: 'JPY',
    name: 'Japanese Yen'
  }, {
    code: 'CAD',
    name: 'Canadian Dollar'
  }, {
    code: 'CHF',
    name: 'Swiss Franc'
  }, {
    code: 'CNY',
    name: 'Chinese Yuan'
  }];
  // Filter sites based on selected entity
  const filteredSites = selectedEntity ? entities.find(e => e.id === selectedEntity)?.sites || [] : [];
  // Handler for creating a new delegation
  const handleCreateDelegation = () => {
    setEditingDelegation(null);
    setShowForm(true);
  };
  // Handler for editing a delegation
  const handleEditDelegation = delegation => {
    setEditingDelegation(delegation);
    setSelectedEntity(delegation.entityId);
    setSelectedSite(delegation.siteId);
    setShowForm(true);
  };
  // Handler for duplicating a delegation
  const handleDuplicateDelegation = delegation => {
    const newDelegation = {
      ...delegation,
      id: `del-${new Date().getTime()}`,
      assignee: `Copy of ${delegation.assignee}`
    };
    setDelegations([...delegations, newDelegation]);
  };
  // Handler for deactivating a delegation
  const handleDeactivateDelegation = delegationId => {
    setDelegations(delegations.map(d => d.id === delegationId ? {
      ...d,
      isActive: false
    } : d));
  };
  // Handler for form submission
  const handleSubmitForm = formData => {
    if (editingDelegation) {
      // Update existing delegation
      setDelegations(delegations.map(d => d.id === editingDelegation.id ? {
        ...formData,
        id: editingDelegation.id
      } : d));
    } else {
      // Create new delegation
      const newDelegation = {
        ...formData,
        id: `del-${new Date().getTime()}`
      };
      setDelegations([...delegations, newDelegation]);
    }
    setShowForm(false);
    setEditingDelegation(null);
  };
  // Render the Delegations tab content
  const renderDelegationsTab = () => {
    if (showForm) {
      return <DelegationForm delegation={editingDelegation} entities={entities} selectedEntity={selectedEntity} setSelectedEntity={setSelectedEntity} selectedSite={selectedSite} setSelectedSite={setSelectedSite} filteredSites={filteredSites} users={users} roles={roles} groups={approvalGroups} glCodes={glCodes} currencies={currencies} onSubmit={handleSubmitForm} onCancel={() => {
        setShowForm(false);
        setEditingDelegation(null);
      }} locale={locale} data-id="element-7292" />;
    }
    return <div className="space-y-4" data-id="element-7293">
        <div className="flex justify-between items-center mb-4" data-id="element-7294">
          <div data-id="element-7295">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-7296">Delegations</h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-7297">
              Manage approval authorities and financial limits
            </p>
          </div>
          <button onClick={handleCreateDelegation} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7298">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-7299" />
            Add Delegation
          </button>
        </div>
        <div className="flex flex-col" data-id="element-7300">
          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8" data-id="element-7301">
            <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8" data-id="element-7302">
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg" data-id="element-7303">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-7304">
                  <thead className="bg-gray-50" data-id="element-7305">
                    <tr data-id="element-7306">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7307">
                        User/Role/Group
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7308">
                        GL Code
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7309">
                        Entity / Site
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7310">
                        Limits
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7311">
                        Approval
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7312">
                        Validity
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7313">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7314">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-7315">
                    {delegations.map(delegation => <tr key={delegation.id} data-id="element-7316">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-7317">
                          <div className="flex items-center" data-id="element-7318">
                            {delegation.type === 'user' ? <UserIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-7319" /> : delegation.type === 'role' ? <UsersIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-7320" /> : <LayersIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-7321" />}
                            <div className="text-sm font-medium text-gray-900" data-id="element-7322">
                              {delegation.assignee}
                            </div>
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-7323">
                            {delegation.type === 'user' ? 'User' : delegation.type === 'role' ? 'Role' : 'Group'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-7324">
                          <div className="text-sm text-gray-900" data-id="element-7325">
                            {delegation.glCode}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-7326">
                            {delegation.glName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-7327">
                          <div className="text-sm text-gray-900" data-id="element-7328">
                            {delegation.entityName}
                          </div>
                          {delegation.siteName && <div className="text-xs text-gray-500" data-id="element-7329">
                              {delegation.siteName}
                            </div>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-7330">
                          <div className="text-sm text-gray-900" data-id="element-7331">
                            {formatCurrency(delegation.lowerLimit, delegation.currency, locale)}{' '}
                            -{' '}
                            {formatCurrency(delegation.upperLimit, delegation.currency, locale)}
                          </div>
                          {delegation.canApproveAboveLimit && <div className="flex items-center text-xs text-gray-500" data-id="element-7332">
                              <ArrowUpRightIcon className="h-3 w-3 mr-1" data-id="element-7333" />
                              Can approve above limit
                            </div>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-7334">
                          {delegation.requiresFourEyes ? <div data-id="element-7335">
                              <div className="flex items-center text-xs text-indigo-600" data-id="element-7336">
                                <UsersIcon className="h-3 w-3 mr-1" data-id="element-7337" />
                                Four Eyes required
                              </div>
                              {delegation.secondApprover && <div className="flex items-center text-xs text-gray-500 mt-1" data-id="element-7338">
                                  <div className="flex items-center" data-id="element-7339">
                                    {delegation.secondApproverType === 'user' ? <UserIcon className="h-3 w-3 mr-1" data-id="element-7340" /> : delegation.secondApproverType === 'role' ? <UsersIcon className="h-3 w-3 mr-1" data-id="element-7341" /> : <LayersIcon className="h-3 w-3 mr-1" data-id="element-7342" />}
                                    Second approver: {delegation.secondApprover}
                                  </div>
                                </div>}
                            </div> : <div className="flex items-center text-xs text-gray-500" data-id="element-7343">
                              <UserCheckIcon className="h-3 w-3 mr-1" data-id="element-7344" />
                              Single approver
                            </div>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-7345">
                          <div className="text-sm text-gray-900" data-id="element-7346">
                            {new Date(delegation.startDate).toLocaleDateString()}
                          </div>
                          {delegation.endDate && <div className="text-xs text-gray-500" data-id="element-7347">
                              to{' '}
                              {new Date(delegation.endDate).toLocaleDateString()}
                            </div>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-7348">
                          {delegation.isActive ? <StatusBadge status="success" text="Active" data-id="element-7349" /> : <StatusBadge status="error" text="Inactive" data-id="element-7350" />}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-7351">
                          <div className="flex justify-end space-x-2" data-id="element-7352">
                            <button onClick={() => handleEditDelegation(delegation)} className="text-indigo-600 hover:text-indigo-900" data-id="element-7353">
                              <EditIcon className="h-4 w-4" data-id="element-7354" />
                            </button>
                            <button onClick={() => handleDuplicateDelegation(delegation)} className="text-blue-600 hover:text-blue-900" data-id="element-7355">
                              <CopyIcon className="h-4 w-4" data-id="element-7356" />
                            </button>
                            {delegation.isActive && <button onClick={() => handleDeactivateDelegation(delegation.id)} className="text-red-600 hover:text-red-900" data-id="element-7357">
                                <XIcon className="h-4 w-4" data-id="element-7358" />
                              </button>}
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
                {delegations.length === 0 && <div className="text-center py-4 text-sm text-gray-500" data-id="element-7359">
                    No delegations found. Click "Add Delegation" to create one.
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>;
  };
  // Render the GL Restrictions tab content
  const renderGLRestrictionsTab = () => {
    return <div className="space-y-4" data-id="element-7360">
        <div className="flex justify-between items-center mb-4" data-id="element-7361">
          <div data-id="element-7362">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-7363">
              GL Restrictions
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-7364">
              Define where users or roles are restricted from approving
            </p>
          </div>
          <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7365">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-7366" />
            Add Restriction
          </button>
        </div>
        <div className="flex flex-col" data-id="element-7367">
          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8" data-id="element-7368">
            <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8" data-id="element-7369">
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg" data-id="element-7370">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-7371">
                  <thead className="bg-gray-50" data-id="element-7372">
                    <tr data-id="element-7373">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7374">
                        User/Role
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7375">
                        GL Code
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7376">
                        Restriction
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7377">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-7378">
                    {glRestrictions.map(restriction => <tr key={restriction.id} data-id="element-7379">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-7380">
                          <div className="flex items-center" data-id="element-7381">
                            {restriction.type === 'user' ? <UserIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-7382" /> : <UsersIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-7383" />}
                            <div className="text-sm font-medium text-gray-900" data-id="element-7384">
                              {restriction.assignee}
                            </div>
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-7385">
                            {restriction.type === 'user' ? 'User' : 'Role'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-7386">
                          <div className="text-sm text-gray-900" data-id="element-7387">
                            {restriction.glCode}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-7388">
                            {restriction.glName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-7389">
                          {restriction.canApprove ? <StatusBadge status="success" text="Can Approve" data-id="element-7390" /> : <StatusBadge status="error" text="Cannot Approve" data-id="element-7391" />}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-7392">
                          <div className="flex justify-end space-x-2" data-id="element-7393">
                            <button className="text-indigo-600 hover:text-indigo-900" data-id="element-7394">
                              <EditIcon className="h-4 w-4" data-id="element-7395" />
                            </button>
                            <button className="text-red-600 hover:text-red-900" data-id="element-7396">
                              <TrashIcon className="h-4 w-4" data-id="element-7397" />
                            </button>
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
                {glRestrictions.length === 0 && <div className="text-center py-4 text-sm text-gray-500" data-id="element-7398">
                    No GL restrictions found. Click "Add Restriction" to create
                    one.
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>;
  };
  // Render the User Leave Status tab content
  const renderLeaveStatusTab = () => {
    return <div className="space-y-4" data-id="element-7399">
        <div className="flex justify-between items-center mb-4" data-id="element-7400">
          <div data-id="element-7401">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-7402">
              User Delegation Status
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-7403">
              Manage user leave and delegation status
            </p>
          </div>
          <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7404">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-7405" />
            Set User Leave
          </button>
        </div>
        <div className="flex flex-col" data-id="element-7406">
          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8" data-id="element-7407">
            <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8" data-id="element-7408">
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg" data-id="element-7409">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-7410">
                  <thead className="bg-gray-50" data-id="element-7411">
                    <tr data-id="element-7412">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7413">
                        User
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7414">
                        Leave Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7415">
                        Leave Period
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7416">
                        Delegation Handling
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7417">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-7418">
                    {userLeaveStatus.map(status => <tr key={status.id} data-id="element-7419">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-7420">
                          <div className="flex items-center" data-id="element-7421">
                            <UserIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-7422" />
                            <div className="text-sm font-medium text-gray-900" data-id="element-7423">
                              {status.userName}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-7424">
                          {status.isOnLeave ? <StatusBadge status="warning" text="On Leave" data-id="element-7425" /> : <StatusBadge status="success" text="Available" data-id="element-7426" />}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-7427">
                          {status.isOnLeave ? <div data-id="element-7428">
                              <div className="text-sm text-gray-900" data-id="element-7429">
                                {new Date(status.leaveStartDate).toLocaleDateString()}
                              </div>
                              <div className="text-xs text-gray-500" data-id="element-7430">
                                to{' '}
                                {new Date(status.leaveEndDate).toLocaleDateString()}
                              </div>
                            </div> : <span className="text-sm text-gray-500" data-id="element-7431">N/A</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-7432">
                          {status.isOnLeave && <div className="text-sm text-gray-900" data-id="element-7433">
                              {status.shouldSkip ? <span className="flex items-center text-amber-600" data-id="element-7434">
                                  <ArrowUpRightIcon className="h-4 w-4 mr-1" data-id="element-7435" />
                                  Auto-escalate approvals
                                </span> : <span className="flex items-center text-green-600" data-id="element-7436">
                                  <CheckIcon className="h-4 w-4 mr-1" data-id="element-7437" />
                                  Keep in approval flow
                                </span>}
                            </div>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-7438">
                          <div className="flex justify-end space-x-2" data-id="element-7439">
                            <button className="text-indigo-600 hover:text-indigo-900" data-id="element-7440">
                              <EditIcon className="h-4 w-4" data-id="element-7441" />
                            </button>
                            {status.isOnLeave && <button className="text-red-600 hover:text-red-900" data-id="element-7442">
                                <XIcon className="h-4 w-4" data-id="element-7443" />
                              </button>}
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
                {userLeaveStatus.length === 0 && <div className="text-center py-4 text-sm text-gray-500" data-id="element-7444">
                    No user leave status found. Click "Set User Leave" to create
                    one.
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>;
  };
  // Render the Approval Rules tab content
  const renderApprovalRulesTab = () => {
    return <div className="space-y-4" data-id="element-7445">
        <div className="flex justify-between items-center mb-4" data-id="element-7446">
          <div data-id="element-7447">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-7448">
              Approval Rules
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-7449">
              Configure approval workflows and escalation rules
            </p>
          </div>
          <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7450">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-7451" />
            Add Rule
          </button>
        </div>
        <div className="flex flex-col" data-id="element-7452">
          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8" data-id="element-7453">
            <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8" data-id="element-7454">
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg" data-id="element-7455">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-7456">
                  <thead className="bg-gray-50" data-id="element-7457">
                    <tr data-id="element-7458">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7459">
                        Delegation
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7460">
                        Level
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7461">
                        Approval Requirements
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7462">
                        Escalation
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7463">
                        Auto-Approve
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7464">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-7465">
                    {approvalRules.map(rule => <tr key={rule.id} data-id="element-7466">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-7467">
                          <div className="text-sm font-medium text-gray-900" data-id="element-7468">
                            {rule.delegationName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-7469">
                          <div className="text-sm text-gray-900" data-id="element-7470">
                            Level {rule.approvalLevel}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-7471">
                          {rule.requiresMultipleApprovers ? <div className="flex items-center text-sm text-gray-900" data-id="element-7472">
                              <UsersIcon className="h-4 w-4 mr-1 text-indigo-500" data-id="element-7473" />
                              Multiple approvers required
                            </div> : <div className="flex items-center text-sm text-gray-900" data-id="element-7474">
                              <UserCheckIcon className="h-4 w-4 mr-1 text-green-500" data-id="element-7475" />
                              Single approver
                            </div>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-7476">
                          {rule.escalationUserName ? <div className="text-sm text-gray-900" data-id="element-7477">
                              {rule.escalationUserName}
                            </div> : <span className="text-sm text-gray-500" data-id="element-7478">None</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-7479">
                          <div className="flex items-center text-sm text-gray-900" data-id="element-7480">
                            <ClockIcon className="h-4 w-4 mr-1 text-amber-500" data-id="element-7481" />
                            After {rule.autoApproveAfter} days
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-7482">
                          <div className="flex justify-end space-x-2" data-id="element-7483">
                            <button className="text-indigo-600 hover:text-indigo-900" data-id="element-7484">
                              <EditIcon className="h-4 w-4" data-id="element-7485" />
                            </button>
                            <button className="text-red-600 hover:text-red-900" data-id="element-7486">
                              <TrashIcon className="h-4 w-4" data-id="element-7487" />
                            </button>
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
                {approvalRules.length === 0 && <div className="text-center py-4 text-sm text-gray-500" data-id="element-7488">
                    No approval rules found. Click "Add Rule" to create one.
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>;
  };
  // Render the Approval Groups tab content
  const renderApprovalGroupsTab = () => {
    return <div className="space-y-4" data-id="element-7489">
        <div className="flex justify-between items-center mb-4" data-id="element-7490">
          <div data-id="element-7491">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-7492">
              Approval Groups
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-7493">
              Manage reusable groups of approvers
            </p>
          </div>
          <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7494">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-7495" />
            Create Group
          </button>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-7496">
          {approvalGroups.map(group => <div key={group.id} className="bg-white overflow-hidden shadow rounded-lg" data-id="element-7497">
              <div className="px-4 py-5 sm:p-6" data-id="element-7498">
                <div className="flex justify-between items-start" data-id="element-7499">
                  <div data-id="element-7500">
                    <h3 className="text-lg font-medium text-gray-900" data-id="element-7501">
                      {group.name}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500" data-id="element-7502">
                      {group.members.length} members
                    </p>
                  </div>
                  <div className="flex space-x-2" data-id="element-7503">
                    <button className="text-indigo-600 hover:text-indigo-900" data-id="element-7504">
                      <EditIcon className="h-4 w-4" data-id="element-7505" />
                    </button>
                    <button className="text-red-600 hover:text-red-900" data-id="element-7506">
                      <TrashIcon className="h-4 w-4" data-id="element-7507" />
                    </button>
                  </div>
                </div>
                <div className="mt-4" data-id="element-7508">
                  <ul className="divide-y divide-gray-200" data-id="element-7509">
                    {group.members.map(member => <li key={member.id} className="py-2 flex justify-between items-center" data-id="element-7510">
                        <div className="flex items-center" data-id="element-7511">
                          <UserIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-7512" />
                          <span className="text-sm text-gray-900" data-id="element-7513">
                            {member.name}
                          </span>
                        </div>
                        <button className="text-red-600 hover:text-red-900" data-id="element-7514">
                          <XIcon className="h-4 w-4" data-id="element-7515" />
                        </button>
                      </li>)}
                  </ul>
                  <button className="mt-4 w-full flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7516">
                    <PlusIcon className="h-4 w-4 mr-2" data-id="element-7517" />
                    Add Member
                  </button>
                </div>
              </div>
            </div>)}
          {approvalGroups.length === 0 && <div className="col-span-full text-center py-4 text-sm text-gray-500" data-id="element-7518">
              No approval groups found. Click "Create Group" to create one.
            </div>}
        </div>
      </div>;
  };
  return <div className="space-y-6" data-id="element-7519">
      <div className="mb-8" data-id="element-7520">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-7521">Delegations</h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-7522">
          Configure and manage financial approval authority, limits, and rules
        </p>
      </div>
      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-7523">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs" data-id="element-7524">
          <button onClick={() => setActiveTab('delegations')} className={`${activeTab === 'delegations' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-7525">
            Delegations
          </button>
          <button onClick={() => setActiveTab('gl-restrictions')} className={`${activeTab === 'gl-restrictions' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-7526">
            GL Restrictions
          </button>
          <button onClick={() => setActiveTab('leave-status')} className={`${activeTab === 'leave-status' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-7527">
            Leave Status
          </button>
          <button onClick={() => setActiveTab('approval-rules')} className={`${activeTab === 'approval-rules' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-7528">
            Approval Rules
          </button>
          <button onClick={() => setActiveTab('approval-groups')} className={`${activeTab === 'approval-groups' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} data-id="element-7529">
            Groups
          </button>
        </nav>
      </div>
      {/* Tab Content */}
      <div className="mt-6" data-id="element-7530">
        {activeTab === 'delegations' && renderDelegationsTab()}
        {activeTab === 'gl-restrictions' && renderGLRestrictionsTab()}
        {activeTab === 'leave-status' && renderLeaveStatusTab()}
        {activeTab === 'approval-rules' && renderApprovalRulesTab()}
        {activeTab === 'approval-groups' && renderApprovalGroupsTab()}
      </div>
    </div>;
}
// Delegation Form Component
function DelegationForm({
  delegation,
  entities,
  selectedEntity,
  setSelectedEntity,
  selectedSite,
  setSelectedSite,
  filteredSites,
  users,
  roles,
  groups,
  glCodes,
  currencies,
  onSubmit,
  onCancel,
  locale
}) {
  const [formData, setFormData] = useState({
    type: delegation?.type || 'user',
    assigneeId: delegation?.assigneeId || '',
    assignee: delegation?.assignee || '',
    glCode: delegation?.glCode || '',
    glName: delegation?.glName || '',
    entityId: delegation?.entityId || selectedEntity || '',
    entityName: delegation?.entityName || '',
    siteId: delegation?.siteId || selectedSite || '',
    siteName: delegation?.siteName || '',
    lowerLimit: delegation?.lowerLimit || 0,
    upperLimit: delegation?.upperLimit || 0,
    canApproveAboveLimit: delegation?.canApproveAboveLimit || false,
    requiresFourEyes: delegation?.requiresFourEyes || false,
    secondApproverId: delegation?.secondApproverId || '',
    secondApprover: delegation?.secondApprover || '',
    secondApproverType: delegation?.secondApproverType || 'user',
    startDate: delegation?.startDate || new Date().toISOString().split('T')[0],
    endDate: delegation?.endDate || '',
    isActive: delegation?.isActive !== undefined ? delegation.isActive : true,
    currency: delegation?.currency || getDefaultCurrency().code
  });
  const [errors, setErrors] = useState({});
  const [selectedGLCode, setSelectedGLCode] = useState(null);
  const [showGLPicker, setShowGLPicker] = useState(false);
  // Create flattened segments for GL code lookup
  const flattenedSegments = useMemo(() => {
    const result = [];
    const flatten = (codes, parentPath = '') => {
      codes.forEach(code => {
        const fullPath = parentPath ? `${parentPath}.${code.code}` : code.code;
        result.push({
          ...code,
          fullGLPath: fullPath,
          segmentName: code.name,
          segmentCode: code.code
        });
        if (code.children && code.children.length > 0) {
          flatten(code.children, fullPath);
        }
      });
    };
    flatten(glCodes);
    return result;
  }, [glCodes]);
  // Handler for form field changes
  const handleChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  // Handler for currency input changes
  const handleCurrencyChange = e => {
    const {
      name,
      value
    } = e.target;
    // Remove non-numeric characters except decimal point
    const sanitizedValue = value.replace(/[^\d.]/g, '');
    setFormData({
      ...formData,
      [name]: sanitizedValue
    });
    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  // Handle entity selection
  const handleEntityChange = e => {
    const entityId = e.target.value;
    setSelectedEntity(entityId);
    setSelectedSite('');
    const entity = entities.find(e => e.id === entityId);
    setFormData({
      ...formData,
      entityId,
      entityName: entity ? entity.name : '',
      siteId: '',
      siteName: ''
    });
  };
  // Handle site selection
  const handleSiteChange = e => {
    const siteId = e.target.value;
    setSelectedSite(siteId);
    const site = filteredSites.find(s => s.id === siteId);
    setFormData({
      ...formData,
      siteId,
      siteName: site ? site.name : ''
    });
  };
  // Handle assignee type change
  const handleTypeChange = e => {
    setFormData({
      ...formData,
      type: e.target.value,
      assigneeId: '',
      assignee: ''
    });
  };
  // Handle assignee selection
  const handleAssigneeChange = e => {
    const assigneeId = e.target.value;
    let assigneeList;
    if (formData.type === 'user') {
      assigneeList = users;
    } else if (formData.type === 'role') {
      assigneeList = roles;
    } else {
      assigneeList = groups;
    }
    const selectedAssignee = assigneeList.find(a => a.id === assigneeId);
    setFormData({
      ...formData,
      assigneeId,
      assignee: selectedAssignee ? selectedAssignee.name : ''
    });
  };
  // Handle second approver type change
  const handleSecondApproverTypeChange = e => {
    setFormData({
      ...formData,
      secondApproverType: e.target.value,
      secondApproverId: '',
      secondApprover: ''
    });
  };
  // Handle second approver selection
  const handleSecondApproverChange = e => {
    const approverId = e.target.value;
    let approverList;
    if (formData.secondApproverType === 'user') {
      approverList = users;
    } else if (formData.secondApproverType === 'role') {
      approverList = roles;
    } else {
      approverList = groups;
    }
    const selectedApprover = approverList.find(a => a.id === approverId);
    setFormData({
      ...formData,
      secondApproverId: approverId,
      secondApprover: selectedApprover ? selectedApprover.name : ''
    });
  };
  // Handle GL code selection
  const handleGLCodeSelect = glCode => {
    setFormData({
      ...formData,
      glCode: glCode.code,
      glName: glCode.name
    });
    setSelectedGLCode(glCode);
    setShowGLPicker(false);
  };
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    if (!formData.assigneeId) {
      newErrors.assigneeId = `Please select a ${formData.type}`;
    }
    if (!formData.glCode) {
      newErrors.glCode = 'Please select a GL code';
    }
    if (!formData.entityId) {
      newErrors.entityId = 'Please select an entity';
    }
    if (formData.upperLimit <= formData.lowerLimit) {
      newErrors.upperLimit = 'Upper limit must be greater than lower limit';
    }
    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }
    if (formData.endDate && new Date(formData.endDate) <= new Date(formData.startDate)) {
      newErrors.endDate = 'End date must be after start date';
    }
    if (!formData.currency) {
      newErrors.currency = 'Currency is required';
    }
    // Validate second approver when Four Eyes is required
    if (formData.requiresFourEyes && !formData.secondApproverId) {
      newErrors.secondApproverId = 'Please select a second approver';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // Handle form submission
  const handleSubmit = e => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };
  // Render GL code tree
  const renderGLCodeTree = (codes, level = 0) => {
    return <ul className={`${level > 0 ? 'ml-4' : ''}`} data-id="element-7531">
        {codes.map(code => <li key={code.id} className="py-1" data-id="element-7532">
            <button type="button" onClick={() => handleGLCodeSelect(code)} className="flex items-center text-left w-full hover:bg-gray-100 p-1 rounded" data-id="element-7533">
              <BookIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-7534" />
              <span className="text-sm font-medium text-gray-900" data-id="element-7535">
                {code.code}
              </span>
              <span className="ml-2 text-sm text-gray-500" data-id="element-7536">{code.name}</span>
            </button>
            {code.children && code.children.length > 0 && renderGLCodeTree(code.children, level + 1)}
          </li>)}
      </ul>;
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-7537">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-7538">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-7539">
          {delegation ? 'Edit Delegation' : 'Create Delegation'}
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-7540">
          {delegation ? 'Update the delegation details below' : 'Configure a new financial approval delegation'}
        </p>
      </div>
      <form onSubmit={handleSubmit} data-id="element-7541">
        <div className="px-4 py-5 sm:p-6" data-id="element-7542">
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-7543">
            {/* Assignment Section */}
            <div className="sm:col-span-6" data-id="element-7544">
              <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-7545">
                Assignment
              </h4>
              <div className="sm:col-span-3 mb-4" data-id="element-7546">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-7547">
                  Assign To
                </label>
                <div className="flex items-center space-x-4" data-id="element-7548">
                  <div className="flex items-center" data-id="element-7549">
                    <input id="user-type" name="type" type="radio" value="user" checked={formData.type === 'user'} onChange={handleTypeChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-7550" />
                    <label htmlFor="user-type" className="ml-2 block text-sm text-gray-700" data-id="element-7551">
                      User
                    </label>
                  </div>
                  <div className="flex items-center" data-id="element-7552">
                    <input id="role-type" name="type" type="radio" value="role" checked={formData.type === 'role'} onChange={handleTypeChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-7553" />
                    <label htmlFor="role-type" className="ml-2 block text-sm text-gray-700" data-id="element-7554">
                      Role
                    </label>
                  </div>
                  <div className="flex items-center" data-id="element-7555">
                    <input id="group-type" name="type" type="radio" value="group" checked={formData.type === 'group'} onChange={handleTypeChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-7556" />
                    <label htmlFor="group-type" className="ml-2 block text-sm text-gray-700" data-id="element-7557">
                      Group
                    </label>
                  </div>
                </div>
              </div>
              <div className="sm:col-span-6" data-id="element-7558">
                <label htmlFor="assigneeId" className="block text-sm font-medium text-gray-700" data-id="element-7559">
                  {formData.type === 'user' ? 'Select User' : formData.type === 'role' ? 'Select Role' : 'Select Group'}
                </label>
                <div className="mt-1" data-id="element-7560">
                  <select id="assigneeId" name="assigneeId" value={formData.assigneeId} onChange={handleAssigneeChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.assigneeId ? 'border-red-300' : ''}`} data-id="element-7561">
                    <option value="" data-id="element-7562">
                      Select{' '}
                      {formData.type === 'user' ? 'a user' : formData.type === 'role' ? 'a role' : 'a group'}
                    </option>
                    {(formData.type === 'user' ? users : formData.type === 'role' ? roles : groups).map(item => <option key={item.id} value={item.id} data-id="element-7563">
                        {item.name}
                      </option>)}
                  </select>
                  {errors.assigneeId && <p className="mt-1 text-sm text-red-600" data-id="element-7564">
                      {errors.assigneeId}
                    </p>}
                </div>
              </div>
              {/* Scope Section */}
              <div className="sm:col-span-6 mt-6" data-id="element-7565">
                <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-7566">
                  Scope
                </h4>
                <div className="mb-4" data-id="element-7567">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-7568">
                    GL Code
                  </label>
                  <div className="mt-1" data-id="element-7569">
                    <GLCodeSelector onChange={value => {
                    // Update form data based on the selection
                    if (value.type === 'full') {
                      const matchingSegment = flattenedSegments.find(s => s.fullGLPath === value.code);
                      setFormData({
                        ...formData,
                        glCode: value.code,
                        glName: matchingSegment?.segmentName || ''
                      });
                    } else {
                      setFormData({
                        ...formData,
                        glCode: value.code,
                        glName: value.segmentName || ''
                      });
                    }
                  }} value={{
                    type: formData.glCode && formData.glCode.includes && formData.glCode.includes('.') ? 'full' : 'segment',
                    code: formData.glCode || '',
                    segmentId: selectedGLCode?.id
                  }} coaData={glCodes} // Pass the chart of accounts data
                  data-id="element-7570" />
                  </div>
                  <p className="mt-1 text-xs text-gray-500" data-id="element-7571">
                    Select a full GL code or specific segment to scope this
                    delegation
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2" data-id="element-7572">
                  <div data-id="element-7573">
                    <label htmlFor="entityId" className="block text-sm font-medium text-gray-700" data-id="element-7574">
                      Entity
                    </label>
                    <div className="mt-1" data-id="element-7575">
                      <select id="entityId" name="entityId" value={formData.entityId} onChange={handleEntityChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.entityId ? 'border-red-300' : ''}`} data-id="element-7576">
                        <option value="" data-id="element-7577">Select an entity</option>
                        {entities.map(entity => <option key={entity.id} value={entity.id} data-id="element-7578">
                            {entity.name}
                          </option>)}
                      </select>
                      {errors.entityId && <p className="mt-1 text-sm text-red-600" data-id="element-7579">
                          {errors.entityId}
                        </p>}
                    </div>
                  </div>
                  <div data-id="element-7580">
                    <label htmlFor="siteId" className="block text-sm font-medium text-gray-700" data-id="element-7581">
                      Site (Optional)
                    </label>
                    <div className="mt-1" data-id="element-7582">
                      <select id="siteId" name="siteId" value={formData.siteId} onChange={handleSiteChange} disabled={!formData.entityId} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-7583">
                        <option value="" data-id="element-7584">All Sites</option>
                        {filteredSites.map(site => <option key={site.id} value={site.id} data-id="element-7585">
                            {site.name}
                          </option>)}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              {/* Limits Section */}
              <div className="sm:col-span-6 mt-6" data-id="element-7586">
                <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-7587">
                  Limits
                </h4>
                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-3" data-id="element-7588">
                  <div data-id="element-7589">
                    <label htmlFor="currency" className="block text-sm font-medium text-gray-700" data-id="element-7590">
                      Currency
                    </label>
                    <div className="mt-1" data-id="element-7591">
                      <select id="currency" name="currency" value={formData.currency} onChange={handleChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.currency ? 'border-red-300' : ''}`} data-id="element-7592">
                        <option value="" data-id="element-7593">Select currency</option>
                        {currencies.map(currency => <option key={currency.code} value={currency.code} data-id="element-7594">
                            {currency.code} - {currency.name}
                          </option>)}
                      </select>
                      {errors.currency && <p className="mt-1 text-sm text-red-600" data-id="element-7595">
                          {errors.currency}
                        </p>}
                    </div>
                  </div>
                  <div data-id="element-7596">
                    <label htmlFor="lowerLimit" className="block text-sm font-medium text-gray-700" data-id="element-7597">
                      Lower Limit
                    </label>
                    <div className="mt-1" data-id="element-7598">
                      <div className="relative rounded-md shadow-sm" data-id="element-7599">
                        <input type="text" name="lowerLimit" id="lowerLimit" value={formData.lowerLimit} onChange={handleCurrencyChange} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pr-12 sm:text-sm border-gray-300 rounded-md" placeholder="0.00" pattern="^\d*(\.\d{0,2})?$" data-id="element-7600" />
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-7601">
                          <span className="text-gray-500 sm:text-sm" data-id="element-7602">
                            {formData.currency}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div data-id="element-7603">
                    <label htmlFor="upperLimit" className="block text-sm font-medium text-gray-700" data-id="element-7604">
                      Upper Limit
                    </label>
                    <div className="mt-1" data-id="element-7605">
                      <div className="relative rounded-md shadow-sm" data-id="element-7606">
                        <input type="text" name="upperLimit" id="upperLimit" value={formData.upperLimit} onChange={handleCurrencyChange} className={`focus:ring-indigo-500 focus:border-indigo-500 block w-full pr-12 sm:text-sm border-gray-300 rounded-md ${errors.upperLimit ? 'border-red-300' : ''}`} placeholder="0.00" pattern="^\d*(\.\d{0,2})?$" data-id="element-7607" />
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-7608">
                          <span className="text-gray-500 sm:text-sm" data-id="element-7609">
                            {formData.currency}
                          </span>
                        </div>
                      </div>
                      {errors.upperLimit && <p className="mt-1 text-sm text-red-600" data-id="element-7610">
                          {errors.upperLimit}
                        </p>}
                    </div>
                  </div>
                </div>
                <div className="mt-4" data-id="element-7611">
                  <div className="flex items-start" data-id="element-7612">
                    <div className="flex items-center h-5" data-id="element-7613">
                      <input id="canApproveAboveLimit" name="canApproveAboveLimit" type="checkbox" checked={formData.canApproveAboveLimit} onChange={handleChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-7614" />
                    </div>
                    <div className="ml-3 text-sm" data-id="element-7615">
                      <label htmlFor="canApproveAboveLimit" className="font-medium text-gray-700" data-id="element-7616">
                        Can approve above limit
                      </label>
                      <p className="text-gray-500" data-id="element-7617">
                        If checked, this delegation can approve transactions
                        above the upper limit with additional authorization.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Approval Requirements Section */}
              <div className="sm:col-span-6 mt-6" data-id="element-7618">
                <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-7619">
                  Approval Requirements
                </h4>
                <div className="mt-4" data-id="element-7620">
                  <div className="flex items-start" data-id="element-7621">
                    <div className="flex items-center h-5" data-id="element-7622">
                      <input id="requiresFourEyes" name="requiresFourEyes" type="checkbox" checked={formData.requiresFourEyes} onChange={handleChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-7623" />
                    </div>
                    <div className="ml-3 text-sm" data-id="element-7624">
                      <label htmlFor="requiresFourEyes" className="font-medium text-gray-700" data-id="element-7625">
                        Four Eyes Approval
                      </label>
                      <p className="text-gray-500" data-id="element-7626">
                        If checked, transactions in this delegation will require
                        a second approver (four eyes principle).
                      </p>
                    </div>
                  </div>
                </div>
                {formData.requiresFourEyes && <div className="mt-4 pl-7 border-l-2 border-indigo-100" data-id="element-7627">
                    <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-7628">
                      Second Approver Configuration
                    </h5>
                    <div className="sm:col-span-3 mb-4" data-id="element-7629">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-7630">
                        Second Approver Type
                      </label>
                      <div className="flex items-center space-x-4" data-id="element-7631">
                        <div className="flex items-center" data-id="element-7632">
                          <input id="second-user-type" name="secondApproverType" type="radio" value="user" checked={formData.secondApproverType === 'user'} onChange={handleSecondApproverTypeChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-7633" />
                          <label htmlFor="second-user-type" className="ml-2 block text-sm text-gray-700" data-id="element-7634">
                            User
                          </label>
                        </div>
                        <div className="flex items-center" data-id="element-7635">
                          <input id="second-role-type" name="secondApproverType" type="radio" value="role" checked={formData.secondApproverType === 'role'} onChange={handleSecondApproverTypeChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-7636" />
                          <label htmlFor="second-role-type" className="ml-2 block text-sm text-gray-700" data-id="element-7637">
                            Role
                          </label>
                        </div>
                        <div className="flex items-center" data-id="element-7638">
                          <input id="second-group-type" name="secondApproverType" type="radio" value="group" checked={formData.secondApproverType === 'group'} onChange={handleSecondApproverTypeChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-7639" />
                          <label htmlFor="second-group-type" className="ml-2 block text-sm text-gray-700" data-id="element-7640">
                            Group
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="sm:col-span-6 mb-2" data-id="element-7641">
                      <label htmlFor="secondApproverId" className="block text-sm font-medium text-gray-700" data-id="element-7642">
                        {formData.secondApproverType === 'user' ? 'Select Second Approver User' : formData.secondApproverType === 'role' ? 'Select Second Approver Role' : 'Select Second Approver Group'}
                      </label>
                      <div className="mt-1" data-id="element-7643">
                        <select id="secondApproverId" name="secondApproverId" value={formData.secondApproverId} onChange={handleSecondApproverChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.secondApproverId ? 'border-red-300' : ''}`} data-id="element-7644">
                          <option value="" data-id="element-7645">
                            Select{' '}
                            {formData.secondApproverType === 'user' ? 'a user' : formData.secondApproverType === 'role' ? 'a role' : 'a group'}
                          </option>
                          {(formData.secondApproverType === 'user' ? users : formData.secondApproverType === 'role' ? roles : groups).map(item => <option key={item.id} value={item.id} data-id="element-7646">
                              {item.name}
                            </option>)}
                        </select>
                        {errors.secondApproverId && <p className="mt-1 text-sm text-red-600" data-id="element-7647">
                            {errors.secondApproverId}
                          </p>}
                      </div>
                    </div>
                  </div>}
              </div>
              {/* Validity Section */}
              <div className="sm:col-span-6 mt-6" data-id="element-7648">
                <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-7649">
                  Validity
                </h4>
                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2" data-id="element-7650">
                  <div data-id="element-7651">
                    <label htmlFor="startDate" className="block text-sm font-medium text-gray-700" data-id="element-7652">
                      Start Date
                    </label>
                    <div className="mt-1" data-id="element-7653">
                      <input type="date" name="startDate" id="startDate" value={formData.startDate} onChange={handleChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.startDate ? 'border-red-300' : ''}`} data-id="element-7654" />
                      {errors.startDate && <p className="mt-1 text-sm text-red-600" data-id="element-7655">
                          {errors.startDate}
                        </p>}
                    </div>
                  </div>
                  <div data-id="element-7656">
                    <label htmlFor="endDate" className="block text-sm font-medium text-gray-700" data-id="element-7657">
                      End Date (Optional)
                    </label>
                    <div className="mt-1" data-id="element-7658">
                      <input type="date" name="endDate" id="endDate" value={formData.endDate} onChange={handleChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.endDate ? 'border-red-300' : ''}`} data-id="element-7659" />
                      {errors.endDate && <p className="mt-1 text-sm text-red-600" data-id="element-7660">
                          {errors.endDate}
                        </p>}
                    </div>
                  </div>
                </div>
                <div className="mt-4" data-id="element-7661">
                  <div className="flex items-start" data-id="element-7662">
                    <div className="flex items-center h-5" data-id="element-7663">
                      <input id="isActive" name="isActive" type="checkbox" checked={formData.isActive} onChange={handleChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-7664" />
                    </div>
                    <div className="ml-3 text-sm" data-id="element-7665">
                      <label htmlFor="isActive" className="font-medium text-gray-700" data-id="element-7666">
                        Is Active
                      </label>
                      <p className="text-gray-500" data-id="element-7667">
                        If unchecked, this delegation will not be used in
                        approval workflows.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 flex justify-end space-x-3" data-id="element-7668">
          <button type="button" onClick={onCancel} className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7669">
            Cancel
          </button>
          <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7670">
            {delegation ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </div>;
}